/**
 * Documents page — list + upload (presigned URL flow) + sync + delete.
 * Upload: request a presigned URL, then PUT the file directly to S3.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Eye, Trash2, RefreshCw, UploadCloud } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { ConfirmDangerButton } from "@/components/common/ConfirmDangerButton";
import {
  DetailFact,
  DetailGrid,
  DetailSection,
  MetadataTable,
  Metric,
  MetricStrip,
  formatDateTime,
  truncateMiddle,
} from "@/components/common/AdminDetails";
import { ActionState, LoadingRow, EmptyState, ErrorState } from "@/components/common/StateViews";
import { DocumentPreview } from "@/components/common/DocumentPreview";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { analyticsApi, collectionApi } from "@/lib/proxy-api";
import { documentApi, type Collection, type CollectionDocumentCount, type Document, type DocumentStatus } from "@/lib/proxy-api";
import { useHasInstance } from "@/lib/use-instance";
import { ApiException } from "@/lib/api-client";
import { canWriteTenant, useTenantWorkspace } from "@/lib/tenant-workspace";

interface SyncResponse {
  message?: string;
  status?: string;
  ingestion_job_id?: string;
}

export function DocumentsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const workspace = useTenantWorkspace();
  const fileInput = useRef<HTMLInputElement>(null);
  const [selectedCollection, setSelectedCollection] = useState("");
  const [viewDocumentId, setViewDocumentId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const hasInstance = useHasInstance();
  const { data: collData, error: collectionsError } = useQuery({
    queryKey: ["collections", workspace.tenantId],
    queryFn: () => collectionApi.list({ tenantId: workspace.tenantId ?? undefined }),
    enabled: hasInstance && workspace.hasTenant,
  });
  const collections = collData?.collections ?? [];
  const collectionIds = collections.map((collection) => collection.id);
  const collectionById = useMemo(
    () => new Map(collections.map((collection) => [collection.id, collection])),
    [collections],
  );
  const selectedCollectionRecord = selectedCollection ? collectionById.get(selectedCollection) : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ["documents", workspace.tenantId, selectedCollection],
    queryFn: () => documentApi.list({
      tenantId: workspace.tenantId ?? undefined,
      collection_id: selectedCollection,
      limit: 200,
    }),
    enabled: hasInstance && workspace.hasTenant && Boolean(selectedCollection) && !isTenantKeyRequired(collectionsError),
    refetchInterval: (query) => {
      const documents = (query.state.data as { documents?: Document[] } | undefined)?.documents ?? [];
      return documents.some((document) => isInFlight(document.processing_status)) ? 10_000 : false;
    },
  });

  const statsQ = useQuery({
    queryKey: ["document-analytics", workspace.tenantId],
    queryFn: () => analyticsApi.documents({ tenantId: workspace.tenantId ?? undefined }),
    enabled: hasInstance && workspace.hasTenant && !isTenantKeyRequired(collectionsError),
  });

  const documentCountsQ = useQuery({
    queryKey: ["collection-document-counts", workspace.tenantId, collectionIds.join("|")],
    queryFn: () => collectionApi.documentCounts({
      tenantId: workspace.tenantId ?? undefined,
      collectionIds,
    }),
    enabled: hasInstance && workspace.hasTenant && collections.length > 0 && !isTenantKeyRequired(collectionsError),
    staleTime: 60_000,
  });

  const documentDetailsQ = useQuery({
    queryKey: ["document", workspace.tenantId, viewDocumentId],
    queryFn: () => documentApi.get(viewDocumentId!, { tenantId: workspace.tenantId ?? undefined }),
    enabled: Boolean(viewDocumentId) && workspace.hasTenant,
  });

  const documentStatusQ = useQuery({
    queryKey: ["document-status", workspace.tenantId, viewDocumentId],
    queryFn: () => documentApi.status(viewDocumentId!, { tenantId: workspace.tenantId ?? undefined }),
    enabled: Boolean(viewDocumentId) && workspace.hasTenant,
    refetchInterval: (query) => {
      const status = (query.state.data as DocumentStatus | undefined)?.processing_status;
      return status && isInFlight(status) ? 10_000 : false;
    },
  });

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    if (!selectedCollection) {
      setUploadError("Select a collection before uploading.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    setActionError(null);
    setActionMessage(null);
    try {
      let uploadedCount = 0;
      const failures: string[] = [];

      for (const file of files) {
        try {
          const contentType = contentTypeForFile(file);
          await documentApi.upload({
            file,
            filename: file.name,
            collection_id: selectedCollection,
            content_type: contentType,
          }, { tenantId: workspace.tenantId ?? undefined, autoSync: false });
          uploadedCount += 1;
        } catch (err) {
          failures.push(`${file.name}: ${err instanceof Error ? err.message : "upload failed"}`);
        }
      }

      if (uploadedCount > 0) {
        try {
          const syncResult = await collectionApi.startIngestion(selectedCollection, { tenantId: workspace.tenantId ?? undefined }) as SyncResponse;
          setActionMessage([
            `${uploadedCount} of ${files.length} file${files.length === 1 ? "" : "s"} uploaded.`,
            syncMessage(syncResult),
            failures.length ? `${failures.length} failed: ${failures.slice(0, 3).join("; ")}${failures.length > 3 ? "; ..." : ""}` : "",
          ].filter(Boolean).join(" "));
        } catch (err) {
          setActionError([
            `${uploadedCount} of ${files.length} file${files.length === 1 ? "" : "s"} uploaded, but collection sync did not start: ${err instanceof Error ? err.message : "unknown error"}.`,
            failures.length ? `${failures.length} upload failed: ${failures.slice(0, 3).join("; ")}${failures.length > 3 ? "; ..." : ""}` : "",
          ].filter(Boolean).join(" "));
        }
      } else {
        setUploadError(`No files uploaded. ${failures.slice(0, 3).join("; ")}${failures.length > 3 ? "; ..." : ""}`);
      }
      qc.invalidateQueries({ queryKey: ["documents", workspace.tenantId, selectedCollection] });
      qc.invalidateQueries({ queryKey: ["collection-document-counts", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["document-analytics", workspace.tenantId] });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed or sync did not start");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
      qc.invalidateQueries({ queryKey: ["documents", workspace.tenantId, selectedCollection] });
      qc.invalidateQueries({ queryKey: ["collection-document-counts", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["document-analytics", workspace.tenantId] });
    }
  }

  const deleteMut = useMutation({
    mutationFn: (id: string) => documentApi.delete(id, { tenantId: workspace.tenantId ?? undefined }),
    onMutate: () => {
      setActionError(null);
      setActionMessage(null);
    },
    onSuccess: (_res, id) => {
      setActionMessage(`Document ${id} deleted.`);
      qc.setQueryData<{ documents: Document[] }>(["documents", workspace.tenantId, selectedCollection], (current) => (
        current ? { ...current, documents: current.documents.filter((document) => String(document.id) !== id) } : current
      ));
      qc.invalidateQueries({ queryKey: ["documents", workspace.tenantId, selectedCollection] });
      qc.invalidateQueries({ queryKey: ["collection-document-counts", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["document-analytics", workspace.tenantId] });
    },
    onError: (err) => setActionError(err instanceof ApiException ? err.message : "Failed to delete document"),
  });

  const syncMut = useMutation({
    mutationFn: (id: string) => documentApi.sync(id, { tenantId: workspace.tenantId ?? undefined }) as Promise<SyncResponse>,
    onMutate: () => {
      setActionError(null);
      setActionMessage(null);
    },
    onSuccess: (res) => {
      setActionMessage(syncMessage(res));
      qc.invalidateQueries({ queryKey: ["documents", workspace.tenantId, selectedCollection] });
      qc.invalidateQueries({ queryKey: ["collection-document-counts", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["document-analytics", workspace.tenantId] });
    },
    onError: (err) => setActionError(err instanceof ApiException ? err.message : "Failed to start document sync"),
  });

  const docs: Document[] = data?.documents ?? [];
  const hasInFlightDocuments = docs.some((document) => isInFlight(document.processing_status));
  const ingestionJobsQ = useQuery({
    queryKey: ["collection-ingestion-jobs", workspace.tenantId, selectedCollection],
    queryFn: () => collectionApi.listJobs(selectedCollection, { tenantId: workspace.tenantId ?? undefined }),
    enabled: hasInstance && workspace.hasTenant && Boolean(selectedCollection) && hasInFlightDocuments && !isTenantKeyRequired(collectionsError),
    refetchInterval: hasInFlightDocuments ? 10_000 : false,
  });

  useEffect(() => {
    if (!ingestionJobsQ.dataUpdatedAt || !selectedCollection) return;
    qc.invalidateQueries({ queryKey: ["documents", workspace.tenantId, selectedCollection] });
    qc.invalidateQueries({ queryKey: ["collection-document-counts", workspace.tenantId] });
    qc.invalidateQueries({ queryKey: ["document-analytics", workspace.tenantId] });
    if (viewDocumentId) {
      qc.invalidateQueries({ queryKey: ["document", workspace.tenantId, viewDocumentId] });
      qc.invalidateQueries({ queryKey: ["document-status", workspace.tenantId, viewDocumentId] });
    }
  }, [ingestionJobsQ.dataUpdatedAt, qc, selectedCollection, viewDocumentId, workspace.tenantId]);

  const analytics = statsQ.data;
  const collectionCounts = documentCountsQ.data?.counts;
  const selectedCollectionCount = selectedCollection ? collectionCounts?.[selectedCollection] : undefined;
  const aggregateCounts = collectionCounts ? aggregateDocumentCounts(collectionCounts) : undefined;
  const selectedCollectionTotal = selectedCollection
    ? selectedCollectionCount?.total ?? 0
    : aggregateCounts?.total ?? analytics?.total_documents ?? 0;
  const tenantTotal = aggregateCounts?.total ?? analytics?.total_documents ?? 0;
  const visibleCount = docs.length;
  const archivedCount = selectedCollection ? selectedCollectionCount?.archived ?? 0 : aggregateCounts?.archived ?? analytics?.archived_documents ?? 0;
  const countsLoading = documentCountsQ.isLoading && collections.length > 0;
  const countsUnavailable = Boolean(documentCountsQ.error);
  const statsUnavailable = countsUnavailable && Boolean(statsQ.error);
  const selectedCountUnavailable = Boolean(selectedCollection) && countsUnavailable;
  const statusCounts = selectedCollection ? selectedCollectionCount?.statuses ?? {} : aggregateCounts?.statuses ?? analytics?.by_status ?? {};
  const statusNote = selectedCollection ? "Selected collection" : aggregateCounts ? "Collection totals" : "Analytics estimate";
  const hasCountedDocuments = countsLoading || countsUnavailable || selectedCollectionTotal > 0 || tenantTotal > 0;

  return (
    <div className="space-y-6">
      <PageHeader title="Documents" description="Files ingested into Bedrock Knowledge Bases." />

      {!workspace.hasTenant ? (
        <EmptyState message="Select a tenant to view documents." />
      ) : isTenantKeyRequired(collectionsError) && workspace.basePath ? (
        <ActionState
          message="This tenant needs a stored Tenant Admin API key before documents can be viewed."
          actionLabel="Open tenant overview"
          onAction={() => navigate(workspace.basePath!)}
        />
      ) : (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4">
          <div className="space-y-1.5">
            <label htmlFor="d-coll" className="text-sm font-medium">Collection</label>
            <select
              id="d-coll"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={selectedCollection}
              onChange={(e) => {
                setSelectedCollection(e.target.value);
                setViewDocumentId(null);
              }}
            >
              <option value="">Select a collection</option>
              {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {canWriteTenant(workspace.role) && (
            <>
              <input ref={fileInput} type="file" multiple onChange={onFile} disabled={uploading} className="hidden" />
              <Button variant="outline" onClick={() => fileInput.current?.click()} disabled={uploading || !selectedCollection}>
                <UploadCloud className="h-4 w-4" />
                {uploading ? "Uploading…" : "Upload files"}
              </Button>
              {!selectedCollection && <p className="text-sm text-muted-foreground">Select a collection before uploading.</p>}
            </>
          )}
          {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}
        </div>
      )}

      {(actionMessage || actionError) && (
        <div className={actionError ? "rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" : "rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground"}>
          {actionError ?? actionMessage}
        </div>
      )}

      {workspace.hasTenant && (isLoading ? (
        <LoadingRow cols={4} />
      ) : error ? (
        isTenantKeyRequired(error) && workspace.basePath ? (
          <ActionState
            message="This tenant needs a stored Tenant Admin API key before documents can be viewed."
            actionLabel="Open tenant overview"
            onAction={() => navigate(workspace.basePath!)}
          />
        ) : (
          <ErrorState message={error instanceof ApiException ? error.message : "Failed to load documents"} />
        )
      ) : docs.length === 0 && !hasCountedDocuments ? (
        <EmptyState message="No documents yet." />
      ) : (
        <div className="space-y-4">
          <MetricStrip>
            <Metric label="Tenant documents" value={countsLoading ? "Loading" : statsUnavailable ? "Unavailable" : tenantTotal} note={aggregateCounts ? "Collection totals" : "Analytics estimate"} />
            <Metric label="Collection documents" value={!selectedCollection ? "-" : countsLoading ? "Loading" : selectedCountUnavailable ? "Unavailable" : formatCount(selectedCollectionCount)} note={selectedCollectionRecord?.name ?? "Select a collection"} />
            <Metric label="Completed" value={statsUnavailable || selectedCountUnavailable ? "Unavailable" : statusCounts.completed ?? 0} note={statusNote} />
            <Metric label="Processing or pending" value={statsUnavailable || selectedCountUnavailable ? "Unavailable" : (statusCounts.processing ?? 0) + (statusCounts.pending ?? 0)} note={statusNote} />
            <Metric label="Failed" value={statsUnavailable || selectedCountUnavailable ? "Unavailable" : statusCounts.failed ?? 0} note={(statusCounts.failed ?? 0) ? "Needs investigation" : statusNote} />
          </MetricStrip>
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            {!selectedCollection
              ? "Select a collection to load document rows. Tenant-wide counts are shown for context only."
              : statsUnavailable
              ? "Showing up to 200 rows. Full counts are unavailable."
              : `Showing ${visibleCount} row${visibleCount === 1 ? "" : "s"} from the first 200 returned by ServisSearch. Full totals are computed server-side from the documents endpoint${archivedCount ? `, including ${archivedCount} archived document${archivedCount === 1 ? "" : "s"}` : ""}. Counts are exact up to 10,000 documents per collection; browsing beyond 200 needs API cursor pagination.`}
          </div>
          {hasInFlightDocuments && (
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              {ingestionJobsQ.error
                ? "Document status is refreshing from the document endpoint only; collection ingestion jobs could not be checked."
                : ingestionJobsQ.isFetching
                ? "Checking collection ingestion jobs for updated processing status."
                : "Collection ingestion jobs are being polled while documents are pending or processing."}
            </div>
          )}

          {!selectedCollection ? (
            <EmptyState message="Select a collection to view its documents." />
          ) : docs.length === 0 ? (
            <EmptyState message="No active document rows match this filter. The counted documents are archived or outside the visible page." />
          ) : (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Filename</TableHead>
                    <TableHead className="w-28">Actions</TableHead>
                    <TableHead>Collection</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Archived</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-16 text-right">Delete</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docs.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>
                        <div className="font-medium">{d.filename}</div>
                        <div className="text-xs text-muted-foreground">{truncateMiddle(d.id, 12)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" title="View document" onClick={() => setViewDocumentId(d.id)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canWriteTenant(workspace.role) && <Button variant="ghost" size="icon" title="Re-sync (ingest)" disabled={syncMut.isPending} onClick={() => syncMut.mutate(String(d.id))}>
                            <RefreshCw className="h-4 w-4" />
                          </Button>}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{collectionName(d.collection_id, collectionById)}</TableCell>
                      <TableCell className="text-muted-foreground">{d.file_type}</TableCell>
                      <TableCell><DocStatusBadge status={d.processing_status} /></TableCell>
                      <TableCell className="text-muted-foreground">{d.is_archived ? "Yes" : "No"}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDateTime(d.created_at)}</TableCell>
                      <TableCell className="text-right">
                        {canWriteTenant(workspace.role) && (
                          <ConfirmDangerButton
                            title="Delete document"
                            resourceName={d.filename}
                            actionLabel="Delete document"
                            disabled={deleteMut.isPending}
                            onConfirm={() => deleteMut.mutate(String(d.id))}
                          >
                            <Trash2 className="h-4 w-4" />
                          </ConfirmDangerButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ))}

      <DocumentDetailsModal
        open={Boolean(viewDocumentId)}
        document={documentDetailsQ.data}
        isLoading={documentDetailsQ.isLoading}
        error={documentDetailsQ.error}
        onClose={() => setViewDocumentId(null)}
        onOpenTenantOverview={workspace.basePath ? () => navigate(workspace.basePath!) : undefined}
        status={documentStatusQ.data}
        statusError={documentStatusQ.error}
        collections={collections}
        tenantId={workspace.tenantId ?? undefined}
      />
    </div>
  );
}

function DocumentDetailsModal({
  open,
  document,
  isLoading,
  error,
  onClose,
  onOpenTenantOverview,
  status,
  statusError,
  collections,
  tenantId,
}: {
  open: boolean;
  document?: Document;
  isLoading: boolean;
  error: unknown;
  onClose: () => void;
  onOpenTenantOverview?: () => void;
  status?: DocumentStatus;
  statusError: unknown;
  collections: Collection[];
  tenantId?: string;
}) {
  if (!open) return null;

  const collectionById = new Map(collections.map((collection) => [collection.id, collection]));
  const primaryCollection = document ? collectionById.get(document.collection_id) : undefined;
  const archived = status?.archive_status?.is_archived ?? document?.is_archived ?? false;
  const archivedAt = archived ? status?.archive_status?.archived_at : undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
      <div className="flex max-h-[88vh] w-full max-w-4xl flex-col rounded-lg border bg-card shadow-lg">
        <div className="flex items-center justify-between gap-3 border-b p-4">
          <div>
            <h2 className="font-semibold">{document?.filename ?? "Document details"}</h2>
            {document && <p className="text-sm text-muted-foreground">{primaryCollection?.name ?? document.collection_id}</p>}
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>

        <div className="overflow-auto p-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading document…</p>
          ) : error ? (
            isTenantKeyRequired(error) && onOpenTenantOverview ? (
              <ActionState
                message="This tenant needs a stored Tenant Admin API key before document details can be viewed."
                actionLabel="Open tenant overview"
                onAction={onOpenTenantOverview}
              />
            ) : (
              <ErrorState message={error instanceof ApiException ? error.message : "Failed to load document"} />
            )
          ) : document ? (
            <div className="space-y-4">
              <DetailSection title="Document overview">
                <DetailGrid>
                  <DetailFact label="Type" value={document.file_type} />
                  <DetailFact label="Status" value={<DocStatusBadge status={status?.processing_status ?? document.processing_status} />} />
                  <DetailFact label="Archived" value={archived ? (archivedAt ? `Yes, since ${formatDateTime(archivedAt)}` : "Yes") : "No"} />
                  <DetailFact label="Created" value={formatDateTime(document.created_at)} />
                </DetailGrid>
              </DetailSection>

              <DetailSection title="Collection placement">
                <DetailGrid>
                  <DetailFact label="Primary Collection" value={primaryCollection?.name ?? document.collection_id} />
                  <DetailFact label="Primary Collection ID" value={truncateMiddle(document.collection_id, 18)} />
                </DetailGrid>
              </DetailSection>

              {status?.error_details && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {status.error_details}
                </div>
              )}
              {Boolean(statusError) && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                  Live processing status could not be loaded, so the status shown may be out of date.
                </div>
              )}

              <DetailSection title="Preview">
                <DocumentPreview key={document.id} documentId={String(document.id)} filename={document.filename} tenantId={tenantId} />
              </DetailSection>

              <DetailSection title="Metadata">
                <MetadataTable value={document.metadata} emptyLabel="No document metadata exposed." />
              </DetailSection>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DocStatusBadge({ status }: { status: string }) {
  const tone =
    status === "completed" ? "bg-green-50 text-green-700 border-green-200"
      : status === "failed" ? "bg-red-50 text-red-700 border-red-200"
        : "bg-amber-50 text-amber-700 border-amber-200";
  return <Badge variant="outline" className={tone}>{status}</Badge>;
}

function isTenantKeyRequired(error: unknown): boolean {
  return error instanceof ApiException && error.code === "TENANT_KEY_REQUIRED";
}

function aggregateDocumentCounts(counts: Record<string, CollectionDocumentCount>): CollectionDocumentCount {
  return Object.values(counts).reduce<CollectionDocumentCount>((total, count) => {
    total.total += count.total;
    total.active += count.active;
    total.archived += count.archived;
    total.truncated = total.truncated || count.truncated;
    for (const [status, statusCount] of Object.entries(count.statuses)) {
      total.statuses[status] = (total.statuses[status] ?? 0) + statusCount;
    }
    return total;
  }, { total: 0, active: 0, archived: 0, statuses: {}, truncated: false });
}

function formatCount(count?: CollectionDocumentCount): string | number {
  if (!count) return 0;
  return count.truncated ? `${count.total}+` : count.total;
}

function collectionName(collectionId: string, collections: Map<string, Collection>): string {
  return collections.get(collectionId)?.name ?? truncateMiddle(collectionId, 14);
}

function isInFlight(status: string): boolean {
  return status === "pending" || status === "processing";
}

function contentTypeForFile(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "md":
      return "text/markdown";
    case "txt":
      return "text/plain";
    case "html":
      return "text/html";
    case "pdf":
      return "application/pdf";
    case "doc":
      return "application/msword";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    default:
      return "application/octet-stream";
  }
}

function syncMessage(response: SyncResponse | undefined): string {
  if (!response) return "Document sync requested.";
  if (response.status === "IN_PROGRESS") {
    return "An ingestion job is already running. The document has been marked processing and should be picked up by the active or next ingestion run.";
  }
  if (response.ingestion_job_id) return `Sync started. Ingestion job ${response.ingestion_job_id}.`;
  return response.message ?? "Document sync requested.";
}
